import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { BiRightArrowAlt } from "react-icons/bi";
import { BsShieldFillCheck } from "react-icons/bs";
import initializeDepartureDate from "../../libs/initializeDepartureDate";
import initializeArrivalDate from "../../libs/initializeArrivalDate";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { nextPage } from "../../config/nextPage";
import { AiOutlineClose } from "react-icons/ai";
import cookieHandler from "../../libs/cookieHandler";
import times from "../../libs/times";

export default function CompanySearchForm({
  open,
  setOpen,
  refer,
  airports,
  defaultAirport,
}) {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectOpen, setSelectOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startTime, setStartTime] = useState("12:30");
  const [endTime, setEndTime] = useState("12:30");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const highlightedItemRef = useRef(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const pathname = location.pathname;
  const getDeparture = params.get("departure");
  const getArrival = params.get("arrival");
  const getPromocode = params.get("promocode");
  const getAirport = params.get("airport");
  const singleProduct = params.get("product");
  const getCustomCid = params.get("cid");
  const [state, setState] = useState("");
  const [validation, setValidation] = useState("");
  const [promocode, setPromoCode] = useState(getPromocode || "");
  const [airport, setAirport] = useState(getAirport || "" || defaultAirport);
const [selectedAirportLabel, setSelectedAirportLabel] = useState("");
  const [departure, setDeparture] = useState(
    initializeDepartureDate(getDeparture || "")
  );
  console.log(getAirport,"getAirport")
  const [arrival, setArrival] = useState(
    initializeArrivalDate(getArrival || "")
  );
  const dropRef = useRef(null);
  const pickerRef = useRef(null);
  const arrivalPickerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Memoize date setting functions to prevent recreation on every render
  const setDepartureDate = useCallback((date) => {
    const depTime = moment(date).format("HH:mm");
    const arrivalTime = moment(arrival).format("HH:mm");
    const depDate = moment(date).format("yyyy-MM-DD");
    const arrivalDate = moment(date).add(7, "days").format("yyyy-MM-DD");

    setDeparture(moment(`${depDate} ${depTime}`).format("yyyy-MM-DD HH:mm"));
    setArrival(
      moment(`${arrivalDate} ${arrivalTime}`).format("yyyy-MM-DD HH:mm")
    );
  }, [arrival]);

  const setArrivalDate = useCallback((date) => {
    setArrival(moment(date).format("yyyy-MM-DD HH:mm"));
  }, []);

  // Memoize airports transformation to prevent recalculation on every render
  const airportsUpdate = useMemo(() => {
    if (!airports?.length) return [];
    return airports.map((airport) => ({
      label: airport?.name,
      value: airport?.code,
      terminal: airport.terminal,
    }));
  }, [airports]);

  const setTime = useCallback((time, mode) => {
    const [h, m] = time.split(":").map((v) => parseInt(v, 10));

    if (mode === "arrival") {
      setArrival(prev =>
        moment(prev)
          .set("hours", h)
          .set("minutes", m)
          .format("yyyy-MM-DD HH:mm")
      );
    } else {
      setDeparture(prev =>
        moment(prev)
          .set("hours", h)
          .set("minutes", m)
          .format("yyyy-MM-DD HH:mm")
      );
    }
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setValidation("");

    if (!airport) {
      setValidation("Please select an airport to continue");
      setState("error");
      return;
    }
    const params = new URLSearchParams({
      departure: departure,
      arrival: arrival,
      promocode: promocode,
      airport,
      product: singleProduct,
    });

    setState("loading");
    nextPage(`${refer}?${params}`);
    if (setOpen) setOpen(false);
    setTimeout(() => setState("initial"), 0);
    if (pathname === refer) {
      setState("");
    }
  }, [airport, departure, arrival, promocode, singleProduct, refer, setOpen, pathname]);




  useEffect(() => {
    if (airport?.length) {
      setValidation("");
    }
  }, [airport]);

  useEffect(() => {
    cookieHandler(getCustomCid);
  }, []);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth < 577; // sm breakpoint
      console.log('CompanySearchForm - Window width:', window.innerWidth, 'isMobile:', isMobileDevice);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Memoize outside click handler to prevent recreation
  const handleOutsideClick = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setSelectOpen(false);
    }
  }, []);

  // Close dropdown when clicking outside - optimized event listener management
  useEffect(() => {
    if (selectOpen && !isMobile) {
      document.addEventListener("mousedown", handleOutsideClick);
      return () => document.removeEventListener("mousedown", handleOutsideClick);
    }
  }, [selectOpen, handleOutsideClick, isMobile]);

  // Memoize computed values that are used in render
  const departureDate = useMemo(() => moment(departure).toDate(), [departure]);
  const arrivalDate = useMemo(() => moment(arrival).toDate(), [arrival]);
  const departureTime = useMemo(() => moment(departure).format("HH:mm"), [departure]);
  const arrivalTime = useMemo(() => moment(arrival).format("HH:mm"), [arrival]);
  const minArrivalDate = useMemo(() => moment(departure).toDate(), [departure]);

  // Filter airports based on search term
  const filteredAirports = airportsUpdate.filter((airport) =>
    airport.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Keyboard navigation handler
  const handleKeyDown = (e) => {
    if (!selectOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (filteredAirports.length > 0) {
          setHighlightedIndex((prev) =>
            prev < filteredAirports.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (filteredAirports.length > 0) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        }
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredAirports.length) {
          handleAirportSelect(filteredAirports[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setSelectOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
        break;
      default:
        // Type to search - capture any alphanumeric key
        if (e.key.length === 1 && /[a-zA-Z0-9\s]/.test(e.key)) {
          e.preventDefault();
          setSearchTerm((prev) => prev + e.key);
          setHighlightedIndex(0);
        } else if (e.key === "Backspace") {
          e.preventDefault();
          setSearchTerm((prev) => prev.slice(0, -1));
          setHighlightedIndex(0);
        }
        break;
    }
  };

  useEffect(() => {
  if (airport && airportsUpdate.length > 0) {
    const found = airportsUpdate.find(a => a.value === airport);
    if (found) {
      setSelectedAirportLabel(found.label);
    }
  }
}, [airport, airportsUpdate]);
  // Airport selection handler
  const handleAirportSelect = useCallback((airport) => {
    setAirport(airport.value);
    setSelectedAirportLabel(airport.label);
    setValidation("");
    setSelectOpen(false);
    setSearchTerm("");
    setHighlightedIndex(-1);
  }, []);

  // Optimized modal close handler to prevent multiple state updates
  const handleModalClose = useCallback(() => {
    if (!open) return; // Prevent multiple close calls
    if (setOpen) {
      setOpen(false);
      // Close any open dropdowns immediately
      setSelectOpen(false);
      setSearchTerm("");
    }
  }, [open, setOpen]);

  // Handle animation when opening/closing modal
  useEffect(() => {
    if (open) {
      setIsAnimating(true);
    } else {
      // Reduced delay for faster close - matches CSS transition duration
      const timer = setTimeout(() => setIsAnimating(false), 150);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Reset highlighted index when search term changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  // Auto-scroll to highlighted item
  useEffect(() => {
    if (highlightedItemRef.current) {
      highlightedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [highlightedIndex]);

  // Make date picker input readonly on mobile to prevent keyboard issues
  useEffect(() => {
    if (isMobile) {
      if (pickerRef.current !== null) {
        pickerRef.current.input.readOnly = true;
      }
      if (arrivalPickerRef.current !== null) {
        arrivalPickerRef.current.input.readOnly = true;
      }
    } else {
      // Remove readonly when not mobile
      if (pickerRef.current !== null) {
        pickerRef.current.input.readOnly = false;
      }
      if (arrivalPickerRef.current !== null) {
        arrivalPickerRef.current.input.readOnly = false;
      }
    }
  }, [isMobile, pickerRef, arrivalPickerRef]);

  // Don't render modal if not open and not animating
  if (!open && !isAnimating) {
    return null;
  }

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: pathname.includes('/booking') ? 9999 : 997 }}
    >
      {/* Modal Backdrop with fade animation */}
      <div 
        className={`absolute inset-0 bg-black transition-all duration-250 ease-in-out ${
          open ? 'bg-opacity-50' : 'bg-opacity-0'
        }`}
        onClick={handleModalClose}
      />
      
      {/* Modal Content Container */}
      <div className="relative z-10">
        {/* Modal Content with height expansion from center */}
        <div 
          className={`bg-white rounded-xl shadow-2xl w-full max-w-md relative transition-all duration-300 ease-out ${
            open 
              ? 'h-auto min-h-[200px] max-h-[90vh] opacity-100 scale-100' 
              : 'h-0 min-h-0 max-h-0 opacity-0 scale-95'
          }`}
          style={{
            transformOrigin: 'center center',
            overflow: open ? 'visible' : 'hidden'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Content Wrapper with proper overflow handling */}
          <div className={`transition-all duration-300 ease-out ${
            open ? 'opacity-100 p-0 overflow-y-auto max-h-[90vh]' : 'opacity-0 p-0 overflow-hidden max-h-0'
          }`}>
          {/* Close Button */}
          <button
            onClick={handleModalClose}
            className="absolute z-10 flex items-center justify-center w-8 h-8 transition-colors duration-200 bg-gray-100 rounded-full top-4 right-4 hover:bg-gray-200"
          >
            <AiOutlineClose className="text-gray-600 text-[16px] font-semibold" />
          </button>

      <form
        action=""
        className="form-control"
        onSubmit={handleSubmit}
      >
        <div className="px-4 py-6">
          <h2 className="mb-3 text-xl font-bold text-center text-primary">
            Find Airport Parking
          </h2>
          
          {/* Airport Selection - Full Width - Hidden on booking page */}
          {!pathname.includes('/booking') && (
            <div className="flex flex-col w-full name-field">
              <label htmlFor="" className="text-primary font-bold text-[14px] pb-1">
                Airport*
              </label>
              <div
                className={
                  validation.length
                    ? "border-[2px] border-[#FF0909] flex items-center w-full rounded-[5px] bg-secondary"
                    : "input-field flex items-center shadow-md border-[2px] border-[#66D1F4] rounded-[5px] px-2 mt-1 bg-secondary"
                }
              >
                <img
                  alt="Company Search Airport Selection Icon"
                  src={require("../../images/select-airport.png")}
                  className="w-[27px] h-[27px] mx-1 relative cursor-pointer "
                  onClick={() => setSelectOpen(!selectOpen)}
                />
                <div className="relative w-full" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectOpen(!selectOpen);
                    }}
                    onKeyDown={handleKeyDown}
                    className="py-2 w-full sm:text-[16px] text-[14px] text-left bg-secondary h-[38px]"
                  >
                    {selectedAirportLabel || "Select airport"}
                  </button>
                  {selectOpen && !isMobile && (
                    <div className="absolute z-[55] w-full mt-1 bg-white border-[1px] border-solid border-slate-100 shadow-xl rounded-lg overflow-hidden pointer-events-auto">
                      {/* Search Indicator */}
                      {searchTerm && (
                        <div className="sticky top-0 p-2 text-xs font-medium text-center bg-primary/10 text-primary border-b border-slate-200">
                          Searching: "{searchTerm}"
                        </div>
                      )}
                      <ul className="max-h-[200px] overflow-y-auto">
                        {filteredAirports.length > 0 ? (
                          filteredAirports.map((a, index) => (
                            <li
                              key={index}
                              ref={highlightedIndex === index ? highlightedItemRef : null}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAirportSelect(a);
                              }}
                              onMouseEnter={() => setHighlightedIndex(index)}
                              className={`p-2 rounded-md cursor-pointer pointer-events-auto transition-colors ${
                                highlightedIndex === index
                                  ? "bg-primary text-white"
                                  : "hover:bg-slate-200"
                              }`}
                            >
                              {a.label}
                            </li>
                          ))
                        ) : (
                          <li className="p-4 text-center text-gray-500">
                            <div className="text-sm">
                              {searchTerm ? `No results for "${searchTerm}"` : "No airports available"}
                            </div>
                            {searchTerm && (
                              <button
                                onClick={() => {
                                  setSearchTerm("");
                                  setHighlightedIndex(-1);
                                }}
                                className="mt-2 text-xs text-primary hover:underline"
                              >
                                Clear search
                              </button>
                            )}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Mobile Full-Screen Airport Selection */}
                  {selectOpen && isMobile  &&  createPortal(
                    <div className="fixed inset-0 bg-white z-[99999] flex flex-col">
                      <div className="flex items-center justify-between p-4 text-white bg-primary">
                        <h3 className="text-lg font-semibold">Select Airport</h3>
                        <button
                          onClick={() => {
                            setSelectOpen(false);
                            setSearchTerm("");
                          }}
                          className="p-2 transition-colors rounded-full hover:bg-blue-700"
                        >
                          <AiOutlineClose className="text-xl" />
                        </button>
                      </div>

                      <div className="flex-1 overflow-y-auto">
                        {filteredAirports.length > 0 ? (
                          filteredAirports.map((a, index) => (
                            <label
                              key={index}
                              className="flex items-center justify-between px-4 py-3 border-b border-gray-200 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAirportSelect(a);
                              }}
                            >
                              <span className="text-base text-gray-900">
                                {a.label}
                              </span>
                              <input
                                type="radio"
                                name="airport"
                                value={a.value}
                                checked={airport === a.value}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleAirportSelect(a);
                                }}
                                className="w-5 h-5 accent-blue-600"
                              />
                            </label>
                          ))
                        ) : (
                          <div className="p-8 text-center text-gray-500">
                            <div className="mb-2 text-lg">No airports found</div>
                            <div className="text-sm">Try different keywords</div>
                          </div>
                        )}
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              </div>
              {validation && (
                <label
                  className="text-[#FF0909] text-[14px] border-[#FF0909]"
                >
                  {validation}
                </label>
              )}
            </div>
          )}
          
          
                    {/* Drop Off Row - 2 Fields Side by Side */}
                    <div className="flex items-center gap-2 mt-3 small:mt-4 md:flex-row md:gap-2">
                      {/* Drop Off Date */}
                      <div className="flex flex-col flex-1 small:w-full name-field">
                        <label
                          htmlFor=""
                          className="text-primary font-bold text-[14px] pb-1"
                        >
                          Drop Off Date*
                        </label>
                        <div className="input-field flex items-center shadow-md border-[2px] border-[#66D1F4] rounded-[5px] px-2 mt-1 bg-secondary">
                          <img
                            src={require("../../images/date.png")}
                            alt="Company Search Drop Off Date Calendar Icon"
                            className="mx-1 cursor-pointer size-5"
                            onClick={() => pickerRef.current?.setOpen(true)}
                          />
                          <DatePicker
                            dateFormat="EEE, dd MMM yyyy"
                            selected={moment(departure).toDate()}
                            onChange={(date) => date && setDepartureDate(date)}
                            minDate={moment().toDate()}
                            className="ml-1 py-2 w-full sm:text-[16px] text-[14px]  bg-secondary h-[38px]"
                            placeholder="Select drop off date"
                            ref={pickerRef}
                          />
                        </div>
                      </div>
          
                      {/* Drop Off Time */}
                      <div className="flex flex-col small:w-full flex-[0.6] name-field md:mt-0">
                        <label
                          htmlFor=""
                          className="text-primary font-bold text-[14px] pb-1"
                        >
                          Drop Off Time*
                        </label>
                        <div className="relative flex items-center shadow-md border-2 border-[#66D1F4] rounded-[5px] px-2 mt-1 bg-secondary">
                          {/* clock icon */}
                          <img
                            src={require("../../images/time.png")}
                            alt="Company Search Drop Off Time Clock Icon"
                            className="absolute pointer-events-none left-2 size-5"
                          />
          
                          {/* select field */}
                          <select
                            onChange={(e) => setTime(e.target.value, "departure")}
                            value={moment(departure).format("HH:mm")}
                            className="appearance-none w-full py-2 pl-8 pr-5 bg-secondary sm:text-[16px] text-[14px] h-[38px] cursor-pointer"
                          >
                            {times.map((o, index) => (
                              <option key={index}>{o}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
          
                    {/* Pick Up Row - 2 Fields Side by Side */}
                    <div className="flex items-center gap-2 mt-3 small:mt-4 md:flex-row md:gap-2">
                      {/* Pick Up Date */}
                      <div className="flex flex-col flex-1 small:w-full name-field">
                        <label
                          htmlFor=""
                          className="text-primary font-bold text-[14px] pb-1"
                        >
                          Pick Up Date*
                        </label>
                        <div className="input-field flex items-center shadow-md border-[2px] border-[#66D1F4] rounded-[5px] px-2 mt-1 bg-secondary">
                          <img
                            src={require("../../images/date.png")}
                            alt="Company Search Pick Up Date Calendar Icon"
                            className="mx-1 cursor-pointer size-5"
                            onClick={() => arrivalPickerRef.current?.setOpen(true)}
                          />
                          <DatePicker
                            dateFormat="EEE, dd MMM yyyy"
                            selected={moment(arrival).toDate()}
                            minDate={moment(departure).toDate()}
                            onChange={(date) => date && setArrivalDate(date)}
                            ref={arrivalPickerRef}
                            className="ml-1 py-2 w-full sm:text-[16px] text-[14px]  bg-secondary h-[38px]"
                            placeholder="Select pick up date"
                          />
                        </div>
                      </div>
          
                      {/* Pick Up Time */}
                      <div className="flex flex-col flex-[0.6] small:w-full name-field md:mt-0">
                        <label className="text-primary font-bold text-[14px] pb-1">
                          Pick Up Time*
                        </label>
          
                        <div className="relative flex items-center shadow-md border-2 border-[#66D1F4] rounded-[5px] px-2 mt-1 bg-secondary">
                          {/* clock icon */}
                          <img
                            src={require("../../images/time.png")}
                            alt="Company Search Pick Up Time Clock Icon"
                            className="absolute pointer-events-none left-2 size-5"
                          />
          
                          {/* select field */}
                          <select
                            onChange={(e) => setTime(e.target.value, "arrival")}
                            value={moment(arrival).format("HH:mm")}
                            className="appearance-none w-full py-2 pl-8 pr-5 bg-secondary sm:text-[16px] text-[14px] h-[38px] cursor-pointer"
                          >
                            {times.map((o, index) => (
                              <option key={index}>{o}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
          
          {/* Promo Code - Full Width */}
          <div className="flex flex-col w-full mt-2 name-field">
            <label htmlFor="" className="text-primary font-bold text-[14px] pb-1">
              Promo Code
            </label>
            <div className="select-field flex items-center shadow-md border-[2px] border-[#66D1F4] rounded-[5px] px-2 mt-1 bg-secondary">
              <img 
                src={require("../../images/promo.png")} 
                alt="Company Search Promo Code Discount Icon" 
                className="mx-1 size-5"
              />
              <input
                type="text"
                value={promocode}
                placeholder="Enter promo code"
                onChange={(e) => setPromoCode(e.target.value)}
                className="ml-1 py-2 w-full bg-secondary sm:text-[16px] text-[14px] h-[38px]"
              />
            </div>
          </div>

          <div className="mt-6">
            <button
              type="submit"
              className="w-full bg-primary hover:bg-blue-900 text-white font-semibold text-[16px] py-2 px-6 rounded-lg transition-colors duration-200 ease-in-out"
            >
              <div className="flex items-center justify-center">
                {state === "loading" ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 mr-3 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                    <span>Searching...</span>
                  </div>
                ) : (
                  <>
                    <span>Update Airport Parking</span>
                    <BiRightArrowAlt className="text-[24px] ml-2" />
                  </>
                )}
              </div>
            </button>
          </div>
          
          <div className="flex items-center justify-center mt-4 text-sm text-gray-600">
            <BsShieldFillCheck className="text-primary text-[16px] mr-2" />
            <span>
              <span className="font-bold">100%</span> Secure Parking Guaranteed
            </span>
          </div>
        </div>
      </form>
          </div>
        </div>
      </div>
    </div>
  );
  
  return createPortal(modalContent, document.body);
}
